import { call, put, select } from 'typed-redux-saga';
import { ActionType } from 'typesafe-actions';
import { findFieldsWithClientValidationErrors } from '@smart-builder/util';
import { doCreateAsyncActionResource } from '../util';
import {
  MainResourceHandler,
  MainResourceState,
  MainStateSelector,
  SaveChange,
} from './types';

type SaveErrorPayload<TItemBody> = {
  onFailure?: (error: any) => void;
  error?: any;
};

// Save is just a simple update now
export function createSaveAction<
  TItemKey,
  TItemSummary extends TItemKey,
  TItemBody extends TItemKey,
>(
  displayName: string,
  selector: MainStateSelector<TItemKey, TItemSummary, TItemBody>,
  updateAsyncRequest: any, // TODO SM typing
) {
  const saveAsync = doCreateAsyncActionResource<
    SaveChange<TItemBody>,
    SaveChange<TItemBody>,
    SaveErrorPayload<TItemBody>
  >(displayName, 'save');

  function* saveRequestSaga(
    params: ActionType<typeof saveAsync.request>,
  ): Generator {
    const state = (yield select(selector)) as MainResourceState<
      TItemKey,
      TItemSummary,
      TItemBody
    >;

    const { onSuccess, onFailure, reloadSnapshots } = params.payload;

    const key = state.currentBody;
    if (!key) {
      console.error('Attempting to save without a body');
      return;
    }

    // SS 20231020 Note the below will conflict with V3780, V3780's version should be taken
    // Check for any client validation errors before continuing.
    const fieldsWithValidationErrors = findFieldsWithClientValidationErrors(
      state.currentBody,
    );
    if (fieldsWithValidationErrors.length) {
      const firstField = fieldsWithValidationErrors[0];
      const error = {
        errorType: 'failedValidation',
        errorName: firstField.fieldName,
        errorMessage: firstField.client.validationResult!.message,
      };
      return yield put(
        saveAsync.failure({
          error,
          onFailure,
        }),
      );
    }

    yield put(
      updateAsyncRequest({
        fieldName: reloadSnapshots ? 'reloadSnapshots' : '',
        value: reloadSnapshots ? true : undefined,
        op: 'save',
        onSuccess,
        onFailure,
      }),
    );
  }

  function* saveFailureSaga(params: ActionType<typeof saveAsync.failure>) {
    if (params.payload.onFailure) {
      yield call(params.payload.onFailure, params.payload.error);
    }
  }

  const saveFailureHandler: MainResourceHandler<
    TItemKey,
    TItemSummary,
    TItemBody,
    typeof saveAsync.failure
  > = (state, action) => {
    return {
      ...state,
      error: action.payload.error ?? state.error,
    };
  };

  return {
    saveAsync,
    saveRequestSaga,
    saveFailureSaga,
    saveFailureHandler,
  };
}
